import { PortalModal } from "../PortalModal";
import { Calendar, ExternalLink, Award, Twitter, Linkedin, Facebook } from "lucide-react";
import type { Speaker, V1Speaker } from "../../types/speaker.types";

interface SpeakerModalProps {
  speaker: Speaker | V1Speaker;
  onClose: () => void;
}

export const SpeakerModal = ({
  speaker,
  onClose
}: SpeakerModalProps) => {
  // Debug the speaker object
  console.log('SpeakerModal received speaker:', speaker);
  
  // Helper function to detect if this is a V1Speaker
  const isV1Speaker = (speaker: Speaker | V1Speaker): speaker is V1Speaker => {
    // Check for V1-specific fields
    const isV1 = 'sessions' in speaker && Array.isArray(speaker.sessions) && 
                 speaker.sessions.length > 0 && 
                 'formattedDate' in (speaker.sessions[0] as any);
    console.log('Is V1 Speaker:', isV1);
    return isV1;
  };

  // Helper function to get the appropriate social icon
  const getSocialIcon = (platform: string) => {
    switch (platform) {
      case 'twitter':
        return <Twitter size={18} />;
      case 'linkedin':
        return <Linkedin size={18} />;
      case 'facebook':
        return <Facebook size={18} />;
      default:
        return <ExternalLink size={18} />;
    }
  };

  // Helper function to get social links based on speaker type
  const getSocialLinks = () => {
    if (isV1Speaker(speaker)) {
      return {
        twitter: speaker.twitterUrl,
        linkedin: speaker.linkedinUrl,
        facebook: speaker.facebookUrl
      };
    } else {
      return speaker.social;
    }
  };

  // Helper function to get sessions data
  const getSessions = () => {
    console.log('Getting sessions for speaker:', speaker);
    
    // For V1 speakers from the API
    if ('sessions' in speaker && Array.isArray(speaker.sessions)) {
      console.log('Processing V1 speaker sessions:', speaker.sessions);
      
      return speaker.sessions.map(session => {
        const sessionData = {
          id: session.id,
          title: session.title || session.name || 'Untitled Session',
          date: session.formattedDate || session.date || '',
          time: session.formattedTime || session.startTime || '',
          room: session.room || ''
        };
        
        console.log('Mapped session:', sessionData);
        return sessionData;
      });
    }
    
    // For V2 speakers
    return [];
  };

  // Get the image URL, handling both V1 and V2 speaker formats
  const getImageUrl = () => {
    // V1 speakers have imageUrl field
    if (isV1Speaker(speaker)) {
      return speaker.imageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(speaker.name)}&size=200`;
    }
    
    // V2 speakers have image field (can be string or object)
    if (!speaker.image) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(speaker.name)}&size=200`;
    }
    if (typeof speaker.image === 'string') {
      return speaker.image;
    }
    return speaker.image.mediumUrl;
  };

  const socialLinks = getSocialLinks();
  const sessions = getSessions();
  const imageUrl = getImageUrl();

  return (
    <PortalModal isOpen={true} onClose={onClose}>
      <div className="space-y-6">
        <div className="sm:flex sm:items-start gap-6">
          {/* Circular image container with blurred background */}
          <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 relative">
            {/* Blurred background image that fills the container */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={imageUrl}
                alt=""
                className="w-full h-full object-cover blur-xl scale-110 opacity-90"
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>

            {/* Main image - centered and contained within the container */}
            <div className="relative h-full w-full flex items-center justify-center">
              <img
                src={imageUrl}
                alt={speaker.name}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(speaker.name)}&size=200`;
                }}
              />
            </div>
          </div>
          <div className="mt-4 sm:mt-0 flex-1">
            <h2 className="text-xl font-semibold text-gray-900">{speaker.name}</h2>
            {speaker.title && (
              <p className="text-gray-600 mt-1">{speaker.title}</p>
            )}
            {speaker.company && (
              <p className="text-gray-500">{speaker.company}</p>
            )}

            {/* Designations/Badges - only show for V2 speakers */}
            {!isV1Speaker(speaker) && speaker.designations && speaker.designations.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {speaker.designations.map(designation => (
                  <span
                    key={designation}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-50 text-yellow-700 rounded-full text-sm font-medium border border-yellow-200"
                  >
                    <Award size={14} className="text-yellow-600" />
                    {designation}
                  </span>
                ))}
              </div>
            )}
            {/* Social media links */}
            {socialLinks && (socialLinks.twitter || socialLinks.linkedin || socialLinks.facebook) && (
              <div className="flex gap-3 mt-4">
                {socialLinks.twitter && (
                  <a
                    href={socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-blue-400 transition-colors"
                    aria-label="Twitter profile"
                  >
                    {getSocialIcon('twitter')}
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-blue-600 transition-colors"
                    aria-label="LinkedIn profile"
                  >
                    {getSocialIcon('linkedin')}
                  </a>
                )}
                {socialLinks.facebook && (
                  <a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-blue-700 transition-colors"
                    aria-label="Facebook profile"
                  >
                    {getSocialIcon('facebook')}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-4">
          {/* About section */}
          {speaker.bio && (
            <div>
              <h3 className="font-medium text-gray-900">About</h3>
              <p className="mt-2 text-gray-600 leading-relaxed">{speaker.bio}</p>
            </div>
          )}

          {/* Topics section - only show for V2 speakers */}
          {!isV1Speaker(speaker) && speaker.topics && speaker.topics.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900">Topics</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {speaker.topics.map(topic => (
                  <span
                    key={topic}
                    className="px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-600"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* Sessions section */}
          {sessions && sessions.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900">Sessions</h3>
              <div className="space-y-3 mt-2">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">{session.title}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {/* Display formatted date if available */}
                        {session.date && <span>{session.date}</span>}
                        {/* Display time with proper formatting */}
                        {session.time && session.time.trim() && (
                          <span>
                            {/* If time already includes range (has dash), don't add 'at' */}
                            {session.time.includes('-') ? ` ${session.time}` : ` at ${session.time}`}
                          </span>
                        )}
                        {/* Display room with bullet separator */}
                        {session.room && session.room.trim() && (
                          <span className="text-gray-500"> • {session.room}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </PortalModal>
  );
};
